if (!customElements.get('rd-header')) {
  customElements.define('rd-header', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.scrollResizeObserver?.disconnect();
      this.desktopRoot = this.querySelector(':scope > .rd-header__desktop');
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.setupCartCount(signal);
      this.setupMobile(signal);
      if (!this.desktopRoot) return;
      this.disclosures = [...this.desktopRoot.querySelectorAll('[data-header-disclosure]')];
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.editorBlock = null;

      this.disclosures.forEach((details) => {
        const summary = details.querySelector('summary');
        summary.setAttribute('aria-expanded', String(details.open));
        summary.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse' && details.dataset.headerDisclosure !== 'search') this.open(details);
        }, { signal });
        summary.addEventListener('click', (event) => {
          event.preventDefault();
          if (details.open) this.close();
          else this.open(details);
        }, { signal });
        summary.addEventListener('focus', () => {
          if (!this.restoringFocus && summary.matches(':focus-visible')) this.open(details);
        }, { signal });
        summary.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          this.open(details);
          details.querySelector('.rd-header__panel a, .rd-header__panel input, .rd-header__panel button')?.focus();
        }, { signal });
      });

      this.desktopRoot.querySelectorAll('.rd-header__nav a.rd-header__tab').forEach((link) => {
        link.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.close();
        }, { signal });
        link.addEventListener('focus', () => this.close(), { signal });
      });

      this.desktopRoot.addEventListener('pointerleave', () => {
        const keyboardFocusInside = this.desktopRoot.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
        if (!this.editorBlock && !keyboardFocusInside) this.close();
      }, { signal });
      this.desktopRoot.addEventListener('focusout', (event) => {
        if (!this.editorBlock && !this.desktopRoot.contains(event.relatedTarget)) this.close();
      }, { signal });
      this.desktopRoot.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.disclosures.some((details) => details.open)) {
          event.preventDefault();
          event.stopPropagation();
          this.close(true);
        }
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.desktopRoot.contains(event.target)) this.close();
      }, { signal });
      this.desktopRoot.querySelector('[data-header-close]')?.addEventListener('click', () => this.close(true), { signal });

      this.desktopRoot.querySelectorAll('.rd-header__sizeguide-button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
          button.dispatchEvent(new CustomEvent('rd-sizeguide:show', {
            bubbles: true,
            detail: { trigger: button }
          }));
        }, { signal });
      });

      // Use the theme's cart drawer and let a normal link work if it is unavailable.
      this.desktopRoot.querySelector('[data-header-cart]')?.addEventListener('click', (event) => {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer || drawer.dataset.cartType === 'page' || drawer.isCartPage || typeof drawer.toggleDrawerClasses !== 'function') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.close();
        drawer.mainTrigger = event.currentTarget;
        drawer.toggleDrawerClasses();
      }, { signal, capture: true });

      this.updatePanelTop = () => {
        this.desktopRoot.style.setProperty('--rd-header-panel-top', `${Math.max(0, this.desktopRoot.getBoundingClientRect().bottom)}px`);
      };
      this.resizeObserver = new ResizeObserver(this.updatePanelTop);
      this.resizeObserver.observe(this.desktopRoot);
      window.addEventListener('resize', this.updatePanelTop, { signal });
      window.addEventListener('scroll', this.updatePanelTop, { signal, passive: true });
      this.desktop.addEventListener('change', () => this.close(), { signal });

      // Custom elements reconnect when Shopify replaces the section in the editor.
      document.addEventListener('shopify:block:select', (event) => {
        const block = [...this.desktopRoot.querySelectorAll('[data-header-block]')].find((element) => element.dataset.headerBlock === event.detail?.blockId);
        if (!block) return;
        this.editorBlock = block;
        this.open(block.closest('details'));
        block.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }, { signal });
      document.addEventListener('shopify:block:deselect', () => {
        this.editorBlock = null;
        this.close();
      }, { signal });
      this.setupScrollReveal(signal);
      this.updatePanelTop();
    }

    setupCartCount(signal) {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      const buttons = [...this.querySelectorAll('[data-header-cart]')];
      let refreshId = 0;
      const unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        const currentRefresh = ++refreshId;
        try {
          let count = event?.cartData?.item_count;
          // Add responses contain line items, so fetch the total when needed.
          if (!Number.isInteger(count)) {
            const response = await fetch(`${window.routes.cart_url}.js`, { signal });
            if (!response.ok) throw new Error('Unable to refresh the cart count');
            count = (await response.json()).item_count;
          }
          if (signal.aborted || currentRefresh !== refreshId || !Number.isInteger(count)) return;
          buttons.forEach((button) => {
            const badge = button.querySelector('[data-header-cart-count]');
            if (!badge) return;
            badge.textContent = count;
            badge.hidden = count === 0;
            button.setAttribute('aria-label', `${button.dataset.cartLabel} (${count})`);
          });
        } catch (error) {
          if (!signal.aborted) console.error(error);
        }
      });
      signal.addEventListener('abort', unsubscribe, { once: true });
    }

    setupScrollReveal(signal) {
      const section = this.closest('.rd-header-section');
      if (!section) return;

      const mobile = window.matchMedia('(max-width: 989px)');
      const mobileBar = this.querySelector('.rd-header__mobile-main');
      // A sticky header's own bounds no longer identify its original position.
      const scrollAnchor = document.createElement('span');
      scrollAnchor.style.display = 'block';
      scrollAnchor.setAttribute('aria-hidden', 'true');
      section.before(scrollAnchor);
      let headerHeight = 0;
      let previousY = Math.max(0, window.scrollY);
      let restoreFrame = null;

      const updateHeight = () => {
        const bar = mobile.matches ? mobileBar : this.desktopRoot;
        headerHeight = Math.ceil(bar?.getBoundingClientRect().height || 0);
        section.style.setProperty('--rd-header-height', `${headerHeight}px`);
      };

      const updateVisibility = (restore = false) => {
        // The size guide pins the header while it fixes the body for scroll lock.
        // Preserve the pre-drawer scroll state until the page is restored.
        if (document.documentElement.matches('.rd-sizeguide-open')) return;
        if (document.documentElement.matches('.rd-mobile-nav-open, .rd-mobile-search-open')) {
          section.classList.remove('rd-header-section--hidden');
          return;
        }

        const currentY = Math.max(0, window.scrollY);
        const sectionTop = scrollAnchor.getBoundingClientRect().top + currentY;
        const pastHeader = currentY > sectionTop + headerHeight;
        const menuOpen = this.disclosures.some((details) => details.open) ||
          !!this.querySelector('.rd-header__mobile-panel:not([hidden])') ||
          !!this.querySelector('.rd-header__search-drawer[open]') ||
          (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible'));

        const scrollingUp = currentY < previousY - 2;
        // Keep the current colors while sliding out to avoid a white flash.
        if (!pastHeader || menuOpen || scrollingUp || restore === true) {
          this.classList.toggle('rd-header--scrolled', pastHeader);
        }
        if (!pastHeader || menuOpen || restore === true) {
          section.classList.remove('rd-header-section--hidden');
        } else if (currentY > previousY + 2) {
          section.classList.add('rd-header-section--hidden');
        } else if (scrollingUp) {
          section.classList.remove('rd-header-section--hidden');
        }

        if (Math.abs(currentY - previousY) > 2) previousY = currentY;
      };

      updateHeight();
      updateVisibility(true);
      const restoreVisibility = () => {
        window.cancelAnimationFrame(restoreFrame);
        // History restoration can finish after load/pageshow handlers run.
        restoreFrame = window.requestAnimationFrame(() => {
          restoreFrame = null;
          updateHeight();
          updateVisibility(true);
        });
      };
      this.scrollResizeObserver = new ResizeObserver(() => {
        updateHeight();
        updateVisibility();
      });
      this.scrollResizeObserver.observe(this.desktopRoot);
      if (mobileBar) this.scrollResizeObserver.observe(mobileBar);
      mobile.addEventListener('change', updateHeight, { signal });
      window.addEventListener('resize', updateHeight, { signal });
      window.addEventListener('scroll', updateVisibility, { signal, passive: true });
      window.addEventListener('load', restoreVisibility, { signal, once: true });
      window.addEventListener('pageshow', restoreVisibility, { signal });
      signal.addEventListener('abort', () => {
        window.cancelAnimationFrame(restoreFrame);
        scrollAnchor.remove();
        section.classList.remove('rd-header-section--hidden');
        section.style.removeProperty('--rd-header-height');
        this.classList.remove('rd-header--scrolled');
      }, { once: true });
    }

    setupMobile(signal) {
      const mobile = this.querySelector(':scope > .rd-header__mobile');
      const toggle = mobile?.querySelector('.rd-header__nav-toggle');
      const panel = mobile?.querySelector(':scope > .rd-header__mobile-panel');
      const main = mobile?.querySelector('.rd-header__mobile-main');
      if (!toggle || !panel || !main) return;
      const headerSection = this.closest('.rd-header-section');

      const tabs = [...panel.querySelectorAll('.rd-header__disclosure > [role="tab"]')];
      const tabPanels = [...panel.querySelectorAll('.rd-header__tabs > [role="tabpanel"]')];
      const selectTab = (selected, moveFocus = false) => {
        tabs.forEach((tab) => {
          const active = tab === selected;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          const tabPanel = tabPanels.find((item) => item.id === tab.getAttribute('aria-controls'));
          if (tabPanel) tabPanel.hidden = !active;
        });
        if (moveFocus) selected.focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => selectTab(tab), { signal });
        tab.addEventListener('keydown', (event) => {
          let nextIndex;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = tabs.length - 1;
          else return;
          event.preventDefault();
          selectTab(tabs[nextIndex], true);
        }, { signal });
      });

      const desktop = window.matchMedia('(min-width: 990px)');
      let lockedScrollY = 0;
      const setPageScrollLocked = (locked) => {
        const root = document.documentElement;

        if (locked) {
          if (root.classList.contains('rd-mobile-nav-open')) return;
          lockedScrollY = window.scrollY;
          // Fixing the body changes the sticky header's containing block.
          // Preserve its viewport position before locking the page.
          const headerTop = Math.max(0, headerSection?.getBoundingClientRect().top || 0);
          headerSection?.style.setProperty('--rd-header-mobile-locked-top', `${headerTop}px`);
          root.style.setProperty('--rd-mobile-nav-scroll-y', `${lockedScrollY}px`);
          root.classList.add('rd-mobile-nav-open');
          return;
        }

        if (!root.classList.contains('rd-mobile-nav-open')) return;
        root.classList.remove('rd-mobile-nav-open');
        root.style.removeProperty('--rd-mobile-nav-scroll-y');
        headerSection?.style.removeProperty('--rd-header-mobile-locked-top');
        window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
      };
      const updatePosition = () => {
        // Position the panel below the full bar, including its centered spacing.
        const bottom = main.getBoundingClientRect().bottom;
        mobile.style.setProperty('--rd-header-mobile-panel-offset', `${bottom - mobile.getBoundingClientRect().top}px`);
        mobile.style.setProperty('--rd-header-mobile-panel-top', `${Math.max(0, bottom)}px`);
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const viewportTop = window.visualViewport?.offsetTop ?? 0;
        const panelHeight = Math.max(0, viewportTop + viewportHeight - Math.max(viewportTop, bottom));
        mobile.style.setProperty('--rd-header-mobile-panel-height', `${panelHeight}px`);
      };
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let isOpen = false;
      let animation;
      const setOpen = (open, immediate = false) => {
        const wasHidden = panel.hidden;
        const current = getComputedStyle(panel);
        const from = wasHidden
          ? { clipPath: 'inset(0 0 100% 0)' }
          : { clipPath: current.clipPath === 'none' ? 'inset(0 0 0% 0)' : current.clipPath };
        animation?.cancel();
        animation = null;
        isOpen = open;
        toggle.setAttribute('aria-expanded', String(open));
        if (!open && panel.contains(document.activeElement)) toggle.focus({ preventScroll: true });
        panel.inert = !open;
        if (open) {
          selectTab(tabs[0]);
          panel.scrollTop = 0;
        }
        setPageScrollLocked(open);
        // Measure after the header has been pinned (or restored on close).
        if (open || !wasHidden) updatePosition();

        if (immediate || reducedMotion.matches || (!open && wasHidden)) {
          panel.hidden = !open;
          return;
        }

        panel.hidden = false;
        const duration = parseFloat(getComputedStyle(mobile).getPropertyValue('--rd-header-mobile-panel-duration')) || 220;
        animation = panel.animate([
          from,
          open ? { clipPath: 'inset(0 0 0% 0)' } : { clipPath: 'inset(0 0 100% 0)' }
        ], { duration, easing: 'ease-out', fill: 'both' });
        animation.onfinish = () => {
          panel.hidden = !isOpen;
          animation.cancel();
          animation = null;
        };
      };

      signal.addEventListener('abort', () => {
        animation?.cancel();
        setPageScrollLocked(false);
        panel.hidden = true;
        panel.inert = true;
        toggle.setAttribute('aria-expanded', 'false');
      }, { once: true });
      setOpen(false, true);
      toggle.addEventListener('click', () => {
        if (!desktop.matches) setOpen(!isOpen);
      }, { signal });
      mobile.querySelectorAll('.rd-header__sizeguide-button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          setOpen(false);
          button.dispatchEvent(new CustomEvent('rd-sizeguide:show', {
            bubbles: true,
            detail: { trigger: button }
          }));
        }, { signal });
      });
      mobile.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !isOpen) return;
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        toggle.focus({ preventScroll: true });
      }, { signal });
      desktop.addEventListener('change', () => setOpen(false, true), { signal });
      const updateOpenPanel = () => {
        if (!panel.hidden) updatePosition();
      };
      window.addEventListener('resize', updateOpenPanel, { signal });
      window.addEventListener('scroll', updateOpenPanel, { signal, passive: true });
      window.visualViewport?.addEventListener('resize', updateOpenPanel, { signal });
      window.visualViewport?.addEventListener('scroll', updateOpenPanel, { signal, passive: true });
      this.setupMobileSearch(signal, () => setOpen(false, true));
    }

    setupMobileSearch(signal, closeNavigation) {
      const mobile = this.querySelector(':scope > .rd-header__mobile');
      const trigger = mobile?.querySelector('[data-mobile-search-toggle]');
      const drawer = mobile?.querySelector('.rd-header__search-drawer');
      if (!trigger || !drawer) return;

      const root = document.documentElement;
      const desktop = window.matchMedia('(min-width: 990px)');
      let scrollY = 0;
      let scrollLocked = false;
      const restorePage = () => {
        trigger.setAttribute('aria-expanded', 'false');
        if (!scrollLocked) return;
        scrollLocked = false;
        root.classList.remove('rd-mobile-search-open');
        root.style.removeProperty('--rd-mobile-search-scroll-y');
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      };
      const close = (restoreFocus = true) => {
        if (!drawer.open) return;
        drawer.close();
        restorePage();
        if (restoreFocus) trigger.focus({ preventScroll: true });
      };
      const updateViewport = () => {
        if (!drawer.open) return;
        drawer.style.setProperty('--rd-search-height', `${window.visualViewport?.height ?? window.innerHeight}px`);
        drawer.style.setProperty('--rd-search-top', `${window.visualViewport?.offsetTop ?? 0}px`);
      };

      trigger.addEventListener('click', () => {
        if (desktop.matches || drawer.open) return;
        closeNavigation();
        scrollY = window.scrollY;
        // A modal dialog lives above header stacking and clipping contexts.
        drawer.showModal();
        scrollLocked = true;
        root.style.setProperty('--rd-mobile-search-scroll-y', `${scrollY}px`);
        root.classList.add('rd-mobile-search-open');
        trigger.setAttribute('aria-expanded', 'true');
        updateViewport();
        drawer.querySelector('input[type="search"]').focus({ preventScroll: true });
      }, { signal });
      drawer.querySelector('[data-mobile-search-close]').addEventListener('click', () => close(), { signal });
      drawer.addEventListener('cancel', (event) => {
        event.preventDefault();
        close();
      }, { signal });
      drawer.addEventListener('close', () => {
        if (!drawer.open) restorePage();
      }, { signal });
      drawer.addEventListener('click', (event) => {
        if (event.target !== drawer) return;
        const bounds = drawer.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
      }, { signal });
      desktop.addEventListener('change', () => close(false), { signal });
      window.addEventListener('resize', updateViewport, { signal });
      window.visualViewport?.addEventListener('resize', updateViewport, { signal });
      window.visualViewport?.addEventListener('scroll', updateViewport, { signal, passive: true });
      signal.addEventListener('abort', () => {
        close(false);
        restorePage();
      }, { once: true });
    }

    open(selected) {
      if (!this.disclosures.includes(selected) || !this.desktop.matches) return;
      this.disclosures.forEach((details) => {
        details.open = details === selected;
        details.querySelector('summary').setAttribute('aria-expanded', String(details.open));
      });
      this.updatePanelTop?.();
    }

    close(restoreFocus = false) {
      const active = this.disclosures.find((details) => details.open);
      this.disclosures.forEach((details) => {
        details.open = false;
        details.querySelector('summary').setAttribute('aria-expanded', 'false');
      });
      if (restoreFocus && active) {
        this.restoringFocus = true;
        active.querySelector('summary').focus({ preventScroll: true });
        this.restoringFocus = false;
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.scrollResizeObserver?.disconnect();
    }
  });
}
